import {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react'

import { LOCALE } from '@hhgtech/hhg-components/types'
import { Box, BoxProps } from '@mantine/core'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'

import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  BOT_SLUGS_STILL_ENABLE_ADS,
  CATEGORIES_TO_DISABLE_ADS,
  HEALTH_TOOLS_SLUGS_STILL_ENABLE_ADS,
} from 'interfaces/constants/ads'
import { setGlobalTargeting } from 'utils/ads'

import { demandManagerRequest } from './demandManagerRequest'
import {
  StyledAdContainer,
  StyledAdHeader,
  StyledCloseBtn,
} from './index.styled'

export type AdUnitItem = {
  adUnitPath: string
  size: number[] | number[][]
  divId: string
  pos: string[]
  sizeMapping?: [number[], number[][]][]
  contentId?: string
  hideIfHasSubot?: boolean
  customTargetings?: { [key: string]: (string | number) | (string | number)[] }
}
export type Props = {
  className?: string
  style?: CSSProperties
  isHidden?: boolean
  isSticky?: boolean
  refreshInterval?: number
  adDefaultSize?: (number | string)[]
  noWrapper?: boolean
  showCloseBtn?: boolean
  headerBottom?: boolean
  onCloseAd?: () => void
  wrapperProps?: BoxProps
  onlyRefreshInview?: boolean
  adWrapperStyle?: CSSProperties
  adStyle?: CSSProperties
  isVideo?: boolean
  ignoreRefreshAd?: boolean
} & AdUnitItem

const SlotsById: { [k: string]: any } = {}

// const _ForceSlugs = [
//   '/mang-thai/thai-ky/tam-ca-nguyet-3/',
//   '/nuoi-day-con/be-0-1-tuoi/',
//   '/nuoi-day-con/tre-1-5-tuoi/',
//   '/nuoi-day-con/nhi-khoa/',
// ]
const _ForcePos = [
  'TopBanner',
  'MidBanner1',
  'MidBanner2',
  'masthead',
  'Sticky',
]
const _getForceRefreshConfig = (locale: LOCALE, pos: string[]) => {
  // if (typeof window === 'undefined') return false
  if (
    locale === LOCALE.Vietnam &&
    // _ForceSlugs.some((s) => window.location.pathname.startsWith(s)) &&
    _ForcePos.some((p) => pos.includes(p))
  )
    return true
}

const displayAd = ({
  contentId,
  adUnitPath,
  size,
  divId,
  pos,
  sizeMapping,
  hideIfHasSubot,
  customTargetings,
  onSuccess,
}: AdUnitItem & {
  onSuccess?: () => void
  onClose?: () => void
}) => {
  if (!divId || !document.getElementById(divId))
    return console.error('divId is required')
  destroyAdSlot(divId)

  window.googletag = window.googletag || { cmd: [] }
  const { googletag } = window
  googletag.cmd.push(function () {
    const $content_id_container = document.querySelector<HTMLMetaElement>(
      'meta[name="content_id"]',
    )
    const $content_id =
      typeof contentId === 'string' ? contentId : $content_id_container?.content
    const newSlot = googletag.defineSlot(adUnitPath, size, divId)
    // HHD-5712
    if (
      document.querySelector(
        "script[src*='https://micro.rubiconproject.com/prebid/dynamic']",
      )
    ) {
      demandManagerRequest([newSlot])
    }

    if (newSlot) {
      try {
        if (customTargetings) {
          Object.keys(customTargetings).forEach((k) => {
            if (k) newSlot?.setTargeting(k, customTargetings[k])
          })
        }
      } catch (e) {
        console.error(e)
      }
      SlotsById[divId] = newSlot
      setGlobalTargeting(newSlot)
      newSlot.setTargeting('Pos', pos)
      newSlot.addService(googletag.pubads())
      if ($content_id) newSlot.setTargeting('Content_ID', $content_id)
      if (sizeMapping) newSlot.defineSizeMapping(sizeMapping)
      if (hideIfHasSubot && document.getElementById('subot--container')) {
        newSlot.setTargeting('chatbot', true)
      }
      googletag.display(divId)
      if (googletag.pubads().isInitialLoadDisabled()) {
        googletag.pubads().refresh([newSlot])
      }
      onSuccess?.()
    }
  })
}

const destroyAdSlot = (divId: string) => {
  if (SlotsById[divId]) {
    window.googletag = window.googletag || { cmd: [] }
    const { googletag } = window
    googletag.cmd.push(function () {
      googletag.destroySlots([SlotsById[divId]])
      delete SlotsById[divId]
    })
  }
}

const DEFAULT_30S_AD_POS = [
  // 'TopBanner',
  // 'MidBanner1',
  // 'MidBanner2',
  // 'EndBanner',
  'Sticky',
]

const ForwaredAdBlock = forwardRef(function InfinityAdBlock(
  props: Props,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { asPath, locale } = useRouterExtend()
  if (
    (CATEGORIES_TO_DISABLE_ADS[locale] || []).find((i: string) =>
      asPath.startsWith(i),
    ) ||
    (asPath.startsWith('/health-tools/') &&
      !(HEALTH_TOOLS_SLUGS_STILL_ENABLE_ADS[locale] || []).find((i) =>
        encodeURIComponent(decodeURIComponent(asPath)).startsWith(
          encodeURIComponent(i),
        ),
      )) ||
    (asPath.startsWith('/bot/') &&
      !(BOT_SLUGS_STILL_ENABLE_ADS[locale] || []).find((i) =>
        encodeURIComponent(decodeURIComponent(asPath)).startsWith(
          encodeURIComponent(i),
        ),
      ))
  ) {
    return <div ref={ref} />
  }

  return <_ForwaredAdBlock {...props} ref={ref} />
})
const _ForwaredAdBlock = forwardRef(function InfinityAdBlock(
  {
    className,
    adUnitPath,
    size,
    divId,
    pos,
    sizeMapping,
    contentId,
    isHidden,
    hideIfHasSubot,
    isSticky,
    refreshInterval, // seconds
    customTargetings,
    style,
    adDefaultSize,
    noWrapper,
    showCloseBtn,
    onCloseAd,
    headerBottom,
    wrapperProps,
    onlyRefreshInview: onlyRefreshInviewProp,
    ignoreRefreshAd = false,
    adWrapperStyle,
    adStyle,
    isVideo,
  }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { locale } = useRouterExtend()
  const defaultRefreshInterval =
    refreshInterval ||
    (locale === LOCALE.Vietnam
      ? pos.some((p) => DEFAULT_30S_AD_POS.includes(p))
        ? 30
        : undefined
      : undefined)
  const { formatMessage: f } = useIntl()
  const { ref: containerRef, inView } = useInView()
  const adRefreshIntervalRef = useRef<number | null>(null)
  const refreshIntervalRef = useRef<number | undefined>(defaultRefreshInterval)
  refreshIntervalRef.current = _getForceRefreshConfig(locale, pos)
    ? 15
    : defaultRefreshInterval

  const onlyRefreshInview =
    _getForceRefreshConfig(locale, pos) || onlyRefreshInviewProp
  const [localCloseAd, setLocalCloseAd] = useState(false)
  const setupRefreshAd = (seconds: number) => {
    if (adRefreshIntervalRef.current !== null) {
      clearInterval(adRefreshIntervalRef.current)
    }
    adRefreshIntervalRef.current = window.setInterval(() => {
      window.googletag = window.googletag || { cmd: [] }
      const { googletag } = window
      googletag.cmd.push(function () {
        if (SlotsById[divId]) {
          googletag.pubads().refresh([SlotsById[divId]])
        }
      })
    }, seconds * 1000)
  }

  useEffect(() => {
    if (
      inView &&
      onlyRefreshInview &&
      refreshIntervalRef.current &&
      !ignoreRefreshAd
    ) {
      setupRefreshAd(refreshIntervalRef.current)
    } else if (!inView && onlyRefreshInview) {
      if (adRefreshIntervalRef.current !== null) {
        clearInterval(adRefreshIntervalRef.current)
      }
    }
  }, [inView, onlyRefreshInview, ignoreRefreshAd])

  useEffect(() => {
    return () => {
      destroyAdSlot(divId)
      if (adRefreshIntervalRef.current)
        clearInterval(adRefreshIntervalRef.current)
    }
  }, [])

  useEffect(() => {
    if (!isHidden) {
      displayAd({
        contentId,
        adUnitPath,
        size,
        divId,
        pos,
        sizeMapping,
        hideIfHasSubot,
        customTargetings,
        onSuccess: () => {
          refreshIntervalRef.current &&
            !onlyRefreshInview &&
            !ignoreRefreshAd &&
            setupRefreshAd(refreshIntervalRef.current)
          const curDiv = document.getElementById(divId)
          if (curDiv && isVideo) {
            curDiv.setAttribute('data-video', String(Boolean(isVideo)))
          }
        },
      })
    }
  }, [isHidden])
  if (localCloseAd) return null
  const AdContent = (
    <div
      style={{
        position: 'relative',
        width: 'fit-content',
        height: 'fit-content',
        overflow: 'hidden',
        maxWidth: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...adWrapperStyle,
      }}
    >
      <div ref={ref} id={divId} style={adStyle}></div>
    </div>
  )
  if (isSticky) return AdContent
  const minWidth = adDefaultSize
    ? adDefaultSize[0]
    : size
    ? Array.isArray(size[0])
      ? size[0][0]
      : size[0]
    : undefined
  const minHeight = adDefaultSize
    ? adDefaultSize[1]
    : size
    ? Array.isArray(size[0])
      ? size[0][1]
      : (size[1] as number)
    : undefined

  return (
    <Box {...wrapperProps} className={className}>
      {showCloseBtn && (
        <StyledCloseBtn
          onClick={() => {
            onCloseAd?.()
            setLocalCloseAd(true)
          }}
        />
      )}
      <StyledAdContainer
        ref={containerRef}
        className={className}
        style={style}
        data-no-wrapper={!!noWrapper}
        data-header-bottom={!!headerBottom}
      >
        {noWrapper ? (
          <>
            {showCloseBtn && (
              <StyledCloseBtn
                onClick={() => {
                  onCloseAd?.()
                  setLocalCloseAd(true)
                }}
              />
            )}
            {AdContent}
          </>
        ) : (
          <>
            <StyledAdHeader data-header-bottom={!!headerBottom}>
              <img loading="lazy" src="/svg/adsIcon.svg" alt="ad icon" />
              <span data-ads-type>
                {f({ id: 'adBanner.title', defaultMessage: 'Advertisement' })}
              </span>
              {showCloseBtn && (
                <StyledCloseBtn
                  onClick={() => {
                    onCloseAd?.()
                    setLocalCloseAd(true)
                  }}
                />
              )}
            </StyledAdHeader>
            <div
              style={{
                minWidth: minWidth || '100%',
                minHeight: minHeight || '100%',
                position: 'relative',
              }}
            >
              {AdContent}
            </div>
          </>
        )}
      </StyledAdContainer>
    </Box>
  )
})

export const AdBlock = memo(ForwaredAdBlock, (prevProps, nextProps) => {
  return (
    prevProps.adUnitPath === nextProps.adUnitPath &&
    prevProps.divId === nextProps.divId &&
    prevProps.className === nextProps.className &&
    prevProps.isHidden === nextProps.isHidden
  )
})
